import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalizedLabel, getLocalizedDescription } from '@/lib/localization-helpers'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { list_template_variables, create_template_variable } from '@/api/document-templates'
import type { TemplateVariable } from '@/types/document-template'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus } from 'lucide-react'

interface VariableAutocompleteProps {
  position: { top: number; left: number } | null
  searchText: string
  onSelect: (variableKey: string) => void
  onClose: () => void
}

export function VariableAutocomplete({ position, searchText, onSelect, onClose }: VariableAutocompleteProps) {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const [variables, setVariables] = useState<TemplateVariable[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newVariable, setNewVariable] = useState({ key: '', label: '', label_ar: '' })
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Use localized helper functions for rendering label/description

  // Fetch variables with debounce
  const fetchVariables = useCallback(async () => {
    try {
      setLoading(true)
      const trimmedSearch = searchText?.trim() || ''
      console.log('[VariableAutocomplete] Fetching with search:', JSON.stringify(trimmedSearch))
      const response = await list_template_variables({
        search: trimmedSearch || undefined,
        page_size: 15,
        page: 1,
      })
      console.log('[VariableAutocomplete] Got', response.results.length, 'results')
      setVariables(response.results)
      setSelectedIndex(0)
    } catch (error) {
      console.error('[VariableAutocomplete] Fetch error:', error)
      setVariables([])
    } finally {
      setLoading(false)
    }
  }, [searchText])

  useEffect(() => {
    if (!position) {
      console.log('[VariableAutocomplete] Position closed, clearing results')
      setVariables([])
      return
    }
    
    console.log('[VariableAutocomplete] Position active, search:', JSON.stringify(searchText))
    
    // Debounce the search with shorter delay for better UX
    const timer = setTimeout(() => {
      console.log('[VariableAutocomplete] Debounce trigger, fetching...')
      fetchVariables()
    }, 150)
    
    return () => clearTimeout(timer)
  }, [searchText, position, fetchVariables])

  // Auto-scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && scrollContainerRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedIndex])

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!position) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, variables.length))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex === variables.length) {
          openCreateDialog()
        } else if (variables[selectedIndex]) {
          onSelect(variables[selectedIndex].key)
        }
        break
      case 'Escape':
        e.preventDefault()
        onClose()
        break
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [position, selectedIndex, variables])

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      // Don't close if clicking inside dropdown or dialog
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // Check if clicked element is part of a dialog
        const dialogElement = (target as Element)?.closest('[role="dialog"]')
        if (!dialogElement) {
          console.log('[VariableAutocomplete] Outside click detected, closing')
          onClose()
        }
      }
    }

    if (position) {
      document.addEventListener('mousedown', handleOutsideClick)
      return () => document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [position, onClose])

  const openCreateDialog = () => {
    setNewVariable({
      key: searchText || '',
      label: '',
      label_ar: '',
    })
    setShowCreateDialog(true)
  }

  const handleCreateVariable = async () => {
    if (!newVariable.key || !newVariable.label) {
      toast({
        title: t('error') || 'Error',
        description: t('fill_required_fields') || 'Please fill required fields',
        variant: 'destructive',
      })
      return
    }

    // Validate key format (should be like entity.property)
    if (!/^[a-z_][a-z0-9_]*(\.[a-z_][a-z0-9_]*)*$/i.test(newVariable.key)) {
      toast({
        title: t('error') || 'Error',
        description: t('invalid_key_format') || 'Key must be in format: entity.property (e.g., client.name)',
        variant: 'destructive',
      })
      return
    }

    setCreating(true)
    try {
      await create_template_variable(newVariable)
      setShowCreateDialog(false)
      onSelect(newVariable.key)
      toast({ 
        title: t('success') || 'Success',
        description: t('variable_created_successfully') || 'Variable created successfully'
      })
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.response?.data?.message || error?.message
      toast({
        title: t('error_creating_variable') || 'Failed to create variable',
        description: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg),
        variant: 'destructive',
      })
    } finally {
      setCreating(false)
    }
  }

  if (!position) return null

  return (
    <>
      <div
        ref={dropdownRef}
        className='fixed z-50 w-80 rounded-lg border border-border bg-popover shadow-md'
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        {/* Scrollable content area */}
        <div
          ref={scrollContainerRef}
          className='max-h-72 overflow-y-auto'
          style={{ scrollBehavior: 'smooth' }}
        >
          {loading && (
            <div className='flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground'>
              <Loader2 className='w-4 h-4 animate-spin' />
              {t('loading') || 'Loading'}...
            </div>
          )}

          {!loading && variables.length === 0 && (
            <div className='p-4 space-y-3'>
              <p className='text-sm text-muted-foreground'>
                {t('variable_not_found') || 'No variables found'}
              </p>
              <Button
                variant='outline'
                size='sm'
                className='w-full gap-2'
                onClick={openCreateDialog}
              >
                <Plus className='w-4 h-4' />
                {t('create_new_variable') || 'Create'}
              </Button>
            </div>
          )}

          {!loading && variables.length > 0 && (
            <div className='py-1'>
              {variables.map((variable, index) => (
                <div
                  key={variable.id}
                  ref={index === selectedIndex ? selectedItemRef : null}
                  className={`px-3 py-2 mx-1 rounded-md cursor-pointer transition-colors ${
                    index === selectedIndex ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                  onClick={() => onSelect(variable.key)}
                >
                  <div className='flex items-center gap-2 mb-1'>
                    <code className='text-xs font-mono bg-muted px-2 py-0.5 rounded'>
                      {variable.key}
                    </code>
                    <Badge
                      variant={variable.source === 'system' ? 'default' : 'secondary'}
                      className='text-xs'
                    >
                      {variable.source === 'system'
                        ? t('system_variable') || 'System'
                        : t('custom_variable') || 'Custom'}
                    </Badge>
                  </div>
                  <p className='text-sm font-medium text-foreground'>
                    {getLocalizedLabel(variable, i18n.language)}
                  </p>
                  {variable.description && (
                    <p className='text-xs text-muted-foreground truncate'>
                      {getLocalizedDescription(variable, i18n.language)}
                    </p>
                  )}
                </div>
              ))}

              {/* Create new option */}
              <div
                className={`px-3 py-2 mx-1 rounded-md cursor-pointer transition-colors border-t ${
                  selectedIndex === variables.length ? 'bg-accent' : 'hover:bg-accent/50'
                }`}
                onClick={openCreateDialog}
              >
                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <Plus className='w-4 h-4' />
                  {t('create_new_variable') || 'Create new'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Variable Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('create_new_variable') || 'Create Variable'}</DialogTitle>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='key'>
                {t('variable_key') || 'Key'} <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='key'
                value={newVariable.key}
                onChange={(e) => setNewVariable(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                placeholder='client.name'
                disabled={creating}
                autoFocus
              />
              <p className='text-xs text-muted-foreground'>
                {t('key_format_help') || 'Format: entity.property (e.g., client.name, case.number, lawyer.email)'}
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='label'>
                {t('variable_label') || 'Label'} <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='label'
                value={newVariable.label}
                onChange={(e) => setNewVariable(prev => ({ ...prev, label: e.target.value }))}
                placeholder='Client Name'
                disabled={creating}
              />
              <p className='text-xs text-muted-foreground'>
                {t('label_help') || 'Display name for this variable (e.g., "Client Full Name")'}
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='label_ar'>{t('variable_label_ar') || 'Label (Arabic)'}</Label>
              <Input
                id='label_ar'
                value={newVariable.label_ar}
                onChange={(e) => setNewVariable(prev => ({ ...prev, label_ar: e.target.value }))}
                placeholder='اسم الموكل'
                disabled={creating}
              />
              <p className='text-xs text-muted-foreground'>
                {t('label_ar_help') || 'Arabic display name (optional)'}
              </p>
            </div>

            {/* Preview */}
            <div className='p-3 rounded-md bg-muted/50 space-y-2'>
              <p className='text-xs font-medium text-muted-foreground'>{t('preview') || 'Preview'}:</p>
              <div className='flex items-center gap-2'>
                <code className='text-sm font-mono bg-background px-2 py-1 rounded flex-1'>
                  {`{{${newVariable.key || '...'}}}`}
                </code>
              </div>
              {newVariable.label && (
                <p className='text-sm text-foreground'>{newVariable.label}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => setShowCreateDialog(false)} disabled={creating}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateVariable} 
              disabled={creating || !newVariable.key || !newVariable.label}
            >
              {creating && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              {creating ? t('creating_variable') || 'Creating...' : t('create') || 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
